if (!customElements.get('ajc-delivery-map')) {
  class AjcDeliveryMap extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abortController = new AbortController();
      this.records = null;
      this.period = 'year';
      this.selectedState = '';
      this.expanded = false;
      this.refreshDelay = 300000;
      this.lastAttempt = 0;
      this.inView = false;
      this.busy = false;
      this.timeZone = this.dataset.timeZone || 'America/Chicago';
      this.locale = document.documentElement.lang || 'en';
      this.labels = JSON.parse(this.querySelector('[data-ajc-delivery-labels]').textContent);
      this.refs = {};
      for (const key of ['card', 'period-date', 'map-heading', 'map-note', 'markers', 'place-count', 'state-count', 'state-select', 'status', 'places', 'expand', 'updated']) {
        this.refs[key] = this.querySelector(`[data-ajc-${key}]`);
      }
      this.statePaths = new Map(Array.from(this.querySelectorAll('[data-ajc-state]'), path => [path.dataset.ajcState, path]));
      this.stateByName = new Map(Array.from(this.statePaths, ([code, path]) => [this.normalize(path.dataset.stateName), code]));
      this.addEventListener('click', event => this.handleClick(event), { signal: this.abortController.signal });
      this.addEventListener('keydown', event => this.handleKeydown(event), { signal: this.abortController.signal });
      this.addEventListener('pointerover', event => this.handlePointer(event), { signal: this.abortController.signal });
      this.addEventListener('pointerleave', () => this.updateMapHeading(), { signal: this.abortController.signal });
      this.refs['state-select'].addEventListener('change', event => this.chooseState(event.target.value), { signal: this.abortController.signal });
      document.addEventListener('visibilitychange', () => this.updateRefreshSchedule(), { signal: this.abortController.signal });
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting);
        this.updateRefreshSchedule();
      }, { rootMargin: '100px 0px' });
      this.intersectionObserver.observe(this);
      this.updatePeriodLabel();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initializeFeed(), { once: true, signal: this.abortController.signal });
      } else {
        this.initializeFeed();
      }
    }

    disconnectedCallback() {
      this.connected = false;
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.refreshTimer);
    }

    initializeFeed() {
      const script = document.querySelector('[data-ajc-delivery-feed]');
      const section = script?.closest('.shopify-section');
      this.feedSectionId = section?.id.replace(/^shopify-section-/, '');
      if (!script || !this.feedSectionId) {
        this.showFailure();
        return;
      }
      try {
        this.initialFeed = this.parseFeed(script.textContent);
        this.refresh(this.initialFeed);
      } catch (error) {
        this.lastAttempt = Date.now();
        this.showFailure();
        this.updateRefreshSchedule();
      }
    }

    normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('en').replace(/[^\p{L}\p{N}]/gu, '');
    }

    currentDateParts(now = new Date()) {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: this.timeZone, year: 'numeric', month: '2-digit' }).formatToParts(now);
      return {
        year: parts.find(part => part.type === 'year').value,
        month: parts.find(part => part.type === 'month').value
      };
    }

    updatePeriodLabel() {
      const options = this.period === 'year'
        ? { timeZone: this.timeZone, year: 'numeric' }
        : { timeZone: this.timeZone, month: 'long', year: 'numeric' };
      this.refs['period-date'].textContent = new Intl.DateTimeFormat(this.locale, options).format(new Date());
    }

    parseFeed(value) {
      const feed = JSON.parse(value);
      if (!feed || !Array.isArray(feed.records) || !feed.pagination) throw new Error('Invalid delivery feed');
      const current = Number(feed.pagination.current_page);
      const pages = Math.max(1, Number(feed.pagination.pages));
      if (!Number.isInteger(current) || current < 1 || !Number.isInteger(pages) || pages < current || pages > 100) {
        throw new Error('Invalid delivery pagination');
      }
      return feed;
    }

    feedUrl(page, pageParam = 'page', next = null) {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(next || root, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid delivery feed origin');
      url.searchParams.set('section_id', this.feedSectionId);
      url.searchParams.set(pageParam, String(page));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async fetchFeed(page, pageParam = 'page', next = null) {
      const response = await fetch(this.feedUrl(page, pageParam, next), {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Delivery feed unavailable');
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = markup.querySelector('[data-ajc-delivery-feed]');
      if (!script) throw new Error('Delivery feed missing');
      const feed = this.parseFeed(script.textContent);
      if (Number(feed.pagination.current_page) !== page) throw new Error('Delivery feed page mismatch');
      return feed;
    }

    async collectFeed(firstPage) {
      let feed = firstPage || await this.fetchFeed(1);
      if (Number(feed.pagination.current_page) !== 1) feed = await this.fetchFeed(1);
      const records = [...feed.records];
      const pages = Math.max(1, Number(feed.pagination.pages));
      for (let page = 2; page <= pages; page += 1) {
        const pageParam = feed.pagination.page_param || 'page';
        feed = await this.fetchFeed(page, pageParam, feed.pagination.next);
        if (Math.max(1, Number(feed.pagination.pages)) !== pages) throw new Error('Delivery feed changed during pagination');
        records.push(...feed.records);
      }
      return records;
    }

    async refresh(firstPage = null) {
      if (this.busy || !this.connected || !this.feedSectionId) return;
      this.busy = true;
      this.lastAttempt = Date.now();
      this.refs.card.setAttribute('aria-busy', 'true');
      try {
        const records = await this.collectFeed(firstPage);
        if (!this.connected) return;
        this.records = this.cleanRecords(records);
        this.stale = false;
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') this.showFailure();
      } finally {
        this.busy = false;
        this.refs.card.setAttribute('aria-busy', 'false');
        this.updateRefreshSchedule();
      }
    }

    cleanRecords(records) {
      return records.flatMap(record => {
        const city = String(record.city || '').trim();
        const region = String(record.region || '').trim();
        const country = String(record.country_code || '').trim().toUpperCase();
        const month = String(record.month || '').trim();
        let regionCode = String(record.region_code || '').trim().toUpperCase();
        if (!city || !/^[A-Z]{2}$/.test(country) || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return [];
        if (country === 'US') {
          if (!this.statePaths.has(regionCode)) regionCode = this.stateByName.get(this.normalize(region)) || '';
        }
        const regionKey = regionCode || this.normalize(region) || 'unspecified-region';
        const timestamp = Date.parse(record.updated_at || '');
        return [{
          city, region, regionCode, country, month,
          key: `${country}|${regionKey}|${this.normalize(city)}`,
          updatedAt: Number.isFinite(timestamp) ? timestamp : null
        }];
      });
    }

    aggregateRecords(records, now = new Date()) {
      const current = this.currentDateParts(now);
      const month = `${current.year}-${current.month}`;
      const places = new Map();
      for (const record of records) {
        if (record.month > month || record.month.slice(0, 4) !== current.year) continue;
        if (this.period === 'month' && record.month !== month) continue;
        if (!places.has(record.key)) places.set(record.key, record);
      }
      const sorted = Array.from(places.values()).sort((a, b) => a.city.localeCompare(b.city, this.locale, { sensitivity: 'base' }) || a.region.localeCompare(b.region, this.locale));
      const states = new Map();
      for (const place of sorted) {
        if (place.country === 'US' && this.statePaths.has(place.regionCode)) {
          if (!states.has(place.regionCode)) states.set(place.regionCode, []);
          states.get(place.regionCode).push(place);
        }
      }
      return { places: sorted, states };
    }

    render() {
      this.aggregate = this.aggregateRecords(this.records);
      if (this.selectedState && !['international', 'other-us'].includes(this.selectedState) && !this.aggregate.states.has(this.selectedState)) this.selectedState = '';
      const number = new Intl.NumberFormat(this.locale);
      this.refs['place-count'].textContent = number.format(this.aggregate.places.length);
      this.refs['state-count'].textContent = number.format(this.aggregate.states.size);
      this.updatePeriodLabel();
      this.renderStateOptions();
      this.renderMap();
      this.renderPlaces();
      this.renderTimestamp();
    }

    renderStateOptions() {
      const select = this.refs['state-select'];
      const options = document.createDocumentFragment();
      options.append(new Option(this.labels.all_states, ''));
      Array.from(this.aggregate.states.keys())
        .sort((a, b) => this.statePaths.get(a).dataset.stateName.localeCompare(this.statePaths.get(b).dataset.stateName, this.locale))
        .forEach(code => options.append(new Option(this.statePaths.get(code).dataset.stateName, code)));
      if (this.aggregate.places.some(place => place.country !== 'US')) options.append(new Option(this.labels.country_other, 'international'));
      if (this.aggregate.places.some(place => place.country === 'US' && !this.statePaths.has(place.regionCode))) options.append(new Option(this.labels.region_unknown, 'other-us'));
      select.replaceChildren(options);
      select.value = this.selectedState;
      select.disabled = this.aggregate.places.length === 0;
    }

    renderMap() {
      const markers = document.createDocumentFragment();
      for (const [code, path] of this.statePaths) {
        const places = this.aggregate.states.get(code);
        const reached = !!places;
        path.classList.toggle('is-reached', reached);
        path.classList.toggle('is-selected', this.selectedState === code);
        if (reached) {
          const countLabel = places.length === 1 ? this.labels.places_singular : this.labels.places_plural;
          path.setAttribute('role', 'button');
          path.setAttribute('tabindex', '0');
          path.setAttribute('aria-pressed', String(this.selectedState === code));
          path.setAttribute('aria-label', `${this.labels.state_button} ${path.dataset.stateName}: ${places.length} ${countLabel}`);
          const marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          marker.setAttribute('cx', path.dataset.cx);
          marker.setAttribute('cy', path.dataset.cy);
          marker.setAttribute('r', '5.5');
          markers.append(marker);
        } else {
          for (const attr of ['role', 'tabindex', 'aria-pressed', 'aria-label']) path.removeAttribute(attr);
        }
      }
      this.refs.markers.replaceChildren(markers);
      this.updateMapHeading();
    }

    updateMapHeading(code = this.selectedState) {
      const path = this.statePaths.get(code);
      const places = this.aggregate?.states.get(code);
      this.refs['map-heading'].textContent = path?.dataset.stateName || this.labels.map_heading;
      this.refs['map-note'].textContent = places ? `${places.length} ${places.length === 1 ? this.labels.places_singular : this.labels.places_plural}` : '';
    }

    renderPlaces() {
      let places = this.aggregate.places;
      if (this.selectedState === 'international') places = places.filter(place => place.country !== 'US');
      else if (this.selectedState === 'other-us') places = places.filter(place => place.country === 'US' && !this.statePaths.has(place.regionCode));
      else if (this.selectedState) places = this.aggregate.states.get(this.selectedState) || [];
      const visible = this.expanded ? places : places.slice(0, 8);
      const fragment = document.createDocumentFragment();
      for (const place of visible) {
        const item = document.createElement('li');
        const city = document.createElement('span');
        const region = document.createElement('span');
        city.textContent = place.city;
        region.textContent = place.country === 'US'
          ? (place.regionCode || place.region || place.country)
          : [place.regionCode || place.region, place.country].filter(Boolean).join(', ');
        item.append(city, region);
        fragment.append(item);
      }
      this.refs.places.replaceChildren(fragment);
      this.refs.places.hidden = places.length === 0;
      this.refs.expand.hidden = places.length <= 8;
      this.refs.expand.textContent = this.expanded ? this.labels.show_less : this.labels.show_all;
      this.refs.expand.setAttribute('aria-expanded', String(this.expanded));
      this.refs.status.textContent = this.stale ? this.labels.stale : (places.length === 0 ? this.labels.empty : '');
      this.refs.status.hidden = !this.refs.status.textContent;
    }

    renderTimestamp() {
      const timestamps = this.records.map(record => record.updatedAt).filter(timestamp => timestamp !== null);
      const latest = timestamps.length ? Math.max(...timestamps) : null;
      if (!latest) {
        this.refs.updated.textContent = this.labels.records_note;
        return;
      }
      const date = new Date(latest);
      const time = document.createElement('time');
      time.dateTime = date.toISOString();
      time.textContent = new Intl.DateTimeFormat(this.locale, {
        timeZone: this.timeZone,
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      }).format(date);
      this.refs.updated.replaceChildren(document.createTextNode(`${this.labels.updated} `), time);
    }

    chooseState(code) {
      this.selectedState = this.selectedState === code ? '' : code;
      this.expanded = false;
      this.refs['state-select'].value = this.selectedState;
      this.renderMap();
      this.renderPlaces();
    }

    handleClick(event) {
      const period = event.target.closest('[data-ajc-period]');
      if (period) {
        this.period = period.dataset.ajcPeriod;
        this.selectedState = '';
        this.expanded = false;
        this.querySelectorAll('[data-ajc-period]').forEach(button => button.setAttribute('aria-pressed', String(button === period)));
        this.updatePeriodLabel();
        if (this.records) this.render();
        return;
      }
      const state = event.target.closest('[data-ajc-state].is-reached');
      if (state && this.aggregate) {
        this.chooseState(state.dataset.ajcState);
        return;
      }
      if (event.target.closest('[data-ajc-expand]') && this.aggregate) {
        this.expanded = !this.expanded;
        this.renderPlaces();
      }
    }

    handleKeydown(event) {
      const state = event.target.closest('[data-ajc-state].is-reached');
      if (!state || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      this.chooseState(state.dataset.ajcState);
    }

    handlePointer(event) {
      const state = event.target.closest('[data-ajc-state].is-reached');
      this.updateMapHeading(state?.dataset.ajcState || this.selectedState);
    }

    showFailure() {
      if (this.records) {
        this.stale = true;
        this.renderPlaces();
      } else {
        this.refs['place-count'].textContent = '—';
        this.refs['state-count'].textContent = '—';
        this.refs.status.textContent = this.labels.unavailable;
        this.refs.status.hidden = false;
        this.refs['state-select'].disabled = true;
      }
      this.refs.card.setAttribute('aria-busy', 'false');
    }

    updateRefreshSchedule() {
      clearTimeout(this.refreshTimer);
      if (!this.connected || this.busy || !this.feedSectionId || !this.inView || document.visibilityState !== 'visible') return;
      const remaining = Math.max(0, this.refreshDelay - (Date.now() - this.lastAttempt));
      this.refreshTimer = setTimeout(() => this.refresh(), remaining);
    }
  }
  customElements.define('ajc-delivery-map', AjcDeliveryMap);
}
